import { Fragment, useCallback, useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { Unity, useUnityContext } from "react-unity-webgl";
import ApiService from "../../services/ApiService";
import StorageService from "../../services/StorageService";

import { getGameAssetsUrl, getProfilePicture } from "../../utilities/Helpers";
import { useDarknetIdentity } from "../../contexts/IdentityContext";
import { useDarknetSocket } from "../../contexts/SocketContext";
import { PublicKey, Transaction } from "@solana/web3.js";
import { useWallet } from "@solana/wallet-adapter-react";
import * as token from "@solana/spl-token";
import { GameGenre, GameResult, GameStatus } from "../../shared/enums";

// This is needed to avoid a unityInstance is not defined error
declare global {
  interface Window {
    unityInstance: any;
  }
}
window.unityInstance = window.unityInstance || {};

const DEFAULT_ADMIN = process.env.REACT_APP_DEFAULT_ADMIN ?? "";

const PongGameComponent = ({ instanceId }: { instanceId: string }) => {
  const navigate = useNavigate();
  const { publicKey } = useWallet();
  const wallet = useWallet();
  const { authenticated, identity, program } = useDarknetIdentity();
  const { socket } = useDarknetSocket();
  let [speed, setSpeed] = useState<number>(0);
  let [player1, setPlayer1] = useState<Profile>();
  let [player2, setPlayer2] = useState<Profile>();
  let [challengeable, setChallengeable] = useState<boolean>(true);
  const gameAssetsUrl = `/games/1`;

  const gameAssets = {
    loaderUrl: `${gameAssetsUrl}/content/game.loader.js`,
    dataUrl: `${gameAssetsUrl}/content/game.data.unityweb`,
    frameworkUrl: `${gameAssetsUrl}/content/game.framework.js.unityweb`,
    codeUrl: `${gameAssetsUrl}/content/game.wasm.unityweb`,
  }

  const {
    unityProvider,
    sendMessage,
    addEventListener,
    removeEventListener,
    loadingProgression,
    isLoaded,
    UNSAFE__unityInstance, // Needed to avoid a unityInstance is not defined error
    UNSAFE__detachAndUnloadImmediate: detachAndUnloadImmediate, // Using unsafe method due to a bug. https://github.com/jeffreylanters/react-unity-webgl/issues/22
  } = useUnityContext({
    loaderUrl: gameAssets.loaderUrl,
    dataUrl: gameAssets.dataUrl,
    frameworkUrl: gameAssets.frameworkUrl,
    codeUrl: gameAssets.codeUrl,
    productName: "Darknet Pong",
    companyName: "Darknet Games",
    productVersion: "1.0",
    cacheControl: handleCacheControl,
  });

  // This is to avoid an error that crashes everything, need to find a better way to handle it when the package is updated
  window.unityInstance = UNSAFE__unityInstance;

  const [gameData, setGameData] = useState<GameData>();
  const [wagerInfo, setWagerInfo] = useState<{
    wagered: boolean;
    wagerAccount: string;
    wagerAmount: string;
    wagerToken: Token;
    winnings: number;
  }>();

  const handleRematchRequest = useCallback(
    (data: string) => {
      let parsedData: RematchRequest = JSON.parse(data);
      if (parsedData.token) {
        StorageService.set("authToken", parsedData.token);
      }
      navigate(`/pong/${parsedData.instanceId}`);
      window.location.reload();
    },
    [navigate]
  );

  const handleSpeedUpdate = useCallback((speed: string) => {
    setSpeed(Number(speed));
  }, []);

  const handleGameData = useCallback((data: string) => {
    let parsedData: GameData = JSON.parse(data);
    setGameData(parsedData);

    if (!player1) {
      ApiService.get(`/account/${parsedData.player1.address}`).then((res) => {
        if (res && res.success) {
          setPlayer1(res.data);
        }
      });
    }
    if (!player2 && parsedData.player2.address.length > 0) {
      ApiService.get(`/account/${parsedData.player2.address}`).then((res) => {
        if (res && res.success) {
          setPlayer2(res.data);
          setChallengeable(false);
        }
      });
    } else if (parsedData.player2.address.length === 0) {
      setPlayer2(undefined);
    }

    setSpeed(parsedData.ball.speed);

    if (parsedData.status !== "Preparing") {
      setChallengeable(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener("resize", resize);
    resize();

    ApiService.get(`/game/${instanceId}`).then((response) => {
      if (response) {
        if (!response.success) {
          /*(navigate("/");
          window.location.reload();*/
        } else {
          if (response.data.type === GameGenre.Wagered) {
            const wagerAmount = Number(response.data.wagerAmount).toFixed(2);
            const winnings = Number(wagerAmount) * 2;
            const wagerAccount = response.data.wagerAccount;
            const wagerToken = response.data.wagerToken;
            setWagerInfo({
              wagered: true,
              wagerAmount,
              wagerAccount,
              wagerToken,
              winnings,
            });
          }
        }
      }
    });

    return () => {
      window.removeEventListener("resize", resize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoaded) {
      addEventListener("UpdateGameData", handleGameData);
      addEventListener("UpdateBallSpeed", handleSpeedUpdate);

      sendMessage("GameManager", "RegisterServer", window.location.host);
      sendMessage("GameManager", "RegisterInstance", instanceId);

      StorageService.get("authToken").then((authToken: string) => {
        if (authToken) {
          sendMessage("GameManager", "RegisterToken", authToken);
        }
      });

      return () => {
        removeEventListener("UpdateGameData", handleGameData);
        removeEventListener("UpdateBallSpeed", handleSpeedUpdate);
        detachAndUnloadImmediate();
      };
    }
  }, [
    instanceId,
    isLoaded,
    detachAndUnloadImmediate,
    handleGameData,
    addEventListener,
    sendMessage,
    removeEventListener,
    handleSpeedUpdate,
  ]);

  useEffect(() => {
    const isChallengeable = (
      program &&
      authenticated &&
      player1?.address !== identity.account.address &&
      (!player2 || player2.address.length > 0) &&
      (gameData?.status === "Preparing" || gameData?.status === "Waiting"));

    setChallengeable(isChallengeable ?? false);
  }, [
    authenticated,
    gameData?.status,
    identity?.account.address,
    player1,
    player2,
    program,
  ]);

  useEffect(() => {
    if (socket.connected) {
      socket.on("rematch", handleRematchRequest);

      return () => {
        socket.off("rematch", handleRematchRequest);
      };
    }
  }, [handleRematchRequest, socket, instanceId]);

  const resize = () => {
    const unityElement = document.getElementById("unity") as HTMLInputElement;
    const width = parseFloat(window.getComputedStyle(unityElement).width);
    unityElement.setAttribute(
      "style",
      "height: " + (width / 16) * 9 + "px !important"
    );
  };

  function handleCacheControl(url: string) {
    if (url.match(/\.data/) || url.match(/\.bundle/)) {
      return "must-revalidate";
    }
    return "no-store";
  }

  async function handleLeaveGame() {
    if (gameData?.status !== "Preparing" && gameData?.status !== "Waiting" && gameData?.status !== "GameOver") {
      if (gameData?.player1.address === identity.account.address) {
        let warning =
          "Upon leaving this page, the game session will end. If the game is playing, you will forfeit. Are you sure you want to leave?";
        let confirmed = window.confirm(warning);
        if (!confirmed) return;
      }
      if (gameData?.player2.address === identity.account.address) {
        let warning =
          "If the game is playing, you will forfeit. Are you sure you want to leave?";
        let confirmed = window.confirm(warning);
        if (!confirmed) return;
      }
    }

    console.log("leaving game");
    await ApiService.post("/leavegame", { id: instanceId });
    StorageService.remove("authToken"); // Clear the auth token when leaving a game instance
    if (wagerInfo?.wagered) {
      if (
        gameData?.status !== "Playing" &&
        gameData?.status !== "Intermission" &&
        gameData?.status !== "GameOver" &&
        (gameData?.player1.address === identity.account.address || gameData?.player2.address === identity.account.address)
      ) {
        if (!program) return;
        const wagerKey = new PublicKey(wagerInfo.wagerAccount);

        console.log("leaving wagered game");
        console.log("program", program.programId.toString());

        // Darknet default admin
        const admin = new PublicKey(DEFAULT_ADMIN);

        const [wagerVault] = PublicKey.findProgramAddressSync(
          [wagerKey.toBuffer()],
          program.programId
        );

        const [playerKey] = PublicKey.findProgramAddressSync(
          [wagerKey.toBuffer(), publicKey!.toBuffer()],
          program.programId
        );

        const player = await program.account.player.fetch(playerKey);

        // wager options = spl: bool, buy_in: u64, max_players: u8, text: String,
        const refundPlayerTx = new Transaction();
        if (wagerInfo.wagerToken.symbol === "SOL") {
          const refundPlayerIx = await program.methods
            .refundPlayer()
            .accounts({
              admin: admin,
              wager: wagerKey,
              wagerVault: wagerVault,
              player: playerKey,
              // @ts-ignore
              playerWallet: player.wallet,
              authority: publicKey!,
              // @ts-ignore
              token: null,
              // @ts-ignore
              playerTokenAccount: null,
              // @ts-ignore
              authorityTokenAccount: null,
              // @ts-ignore
              wagerVaultTokenAccount: null,
              // @ts-ignore
              tokenProgram: null,
              // @ts-ignore
              associatedTokenProgram: null,
            })
            .instruction();
          refundPlayerTx.add(refundPlayerIx);
        } else {
          const tokenMint = new PublicKey(wagerInfo.wagerToken.address);
          const authorityTokenAccount = await token.getAssociatedTokenAddress(
            tokenMint,
            publicKey!,
            true
          );
          const wagerVaultTokenAccount = await token.getAssociatedTokenAddress(
            tokenMint,
            wagerVault,
            true
          );
          const playerTokenAccount = await token.getAssociatedTokenAddress(
            tokenMint,
            // @ts-ignore
            player.wallet,
            true
          );
          const refundPlayerIx = await program.methods
            .refundPlayer()
            .accounts({
              admin: admin,
              wager: wagerKey,
              wagerVault: wagerVault,
              player: playerKey,
              // @ts-ignore
              playerWallet: player.wallet,
              authority: publicKey!,
              // @ts-ignore
              token: tokenMint,
              // @ts-ignore
              playerTokenAccount: playerTokenAccount,
              // @ts-ignore
              authorityTokenAccount: authorityTokenAccount,
              // @ts-ignore
              wagerVaultTokenAccount: wagerVaultTokenAccount,
              // @ts-ignore
              tokenProgram: token.TOKEN_PROGRAM_ID,
              // @ts-ignore
              associatedTokenProgram: token.ASSOCIATED_TOKEN_PROGRAM_ID,
            })
            .instruction();
          refundPlayerTx.add(refundPlayerIx);
        }
        refundPlayerTx.feePayer = publicKey!;

        const latestBlockHash =
          await program.provider.connection.getLatestBlockhash();
        refundPlayerTx.recentBlockhash = latestBlockHash.blockhash;

        const sig = await wallet.sendTransaction(
          refundPlayerTx,
          program.provider.connection
        );
        const tx = await program.provider.connection.confirmTransaction({
          blockhash: latestBlockHash.blockhash,
          lastValidBlockHeight: latestBlockHash.lastValidBlockHeight,
          signature: sig,
        });
        if (!tx.value.err) {
          console.log("refund success");
          navigate("/");
        } else {
          console.log("refund failed");
        }
      } else {
        console.log("navigate to home but not refunding");
        console.log(wagerInfo?.wagered);
        console.log(gameData?.status);
        navigate("/");
      }
    } else {
      console.log("navigate to home");
      navigate("/");
    }
  }

  async function handleChallengeGame() {
    if (!authenticated) return;
    if (!challengeable) return;

    if (wagerInfo?.wagered && program) {
      const wagerKey = new PublicKey(wagerInfo?.wagerAccount);
      const currentWager = await program.account.wager.fetch(wagerKey);

      console.log("creating wagered game");
      console.log("program", program.programId.toString());
      // @ts-ignore
      console.log("totalWagerCost", currentWager.buyIn);

      // Darknet default admin
      const admin = new PublicKey(DEFAULT_ADMIN);

      const [wagerVault] = PublicKey.findProgramAddressSync(
        [wagerKey.toBuffer()],
        program.programId
      );

      const [player] = PublicKey.findProgramAddressSync(
        [wagerKey.toBuffer(), publicKey!.toBuffer()],
        program.programId
      );

      try {
        const joinWagerTx = new Transaction();
        if (wagerInfo.wagerToken.symbol === "SOL") {
          // wager options = spl: bool, buy_in: u64, max_players: u8, text: String,
          const joinWagerIx = await program.methods
            .joinWager()
            .accounts({
              admin: admin,
              wager: wagerKey,
              wagerVault: wagerVault,
              player: player,
              authority: publicKey!,
              // @ts-ignore
              token: null,
              // @ts-ignore
              authorityTokenAccount: null,
              // @ts-ignore
              wagerVaultTokenAccount: null,
              // @ts-ignore
              tokenProgram: null,
              // @ts-ignore
              associatedTokenProgram: null,
            })
            .instruction();
          joinWagerTx.add(joinWagerIx);
        } else {
          const tokenMint = new PublicKey(wagerInfo.wagerToken.address);
          const authorityTokenAccount = await token.getAssociatedTokenAddress(
            tokenMint,
            publicKey!,
            true
          );
          const wagerVaultTokenAccount = await token.getAssociatedTokenAddress(
            tokenMint,
            wagerVault,
            true
          );
          const joinWagerIx = await program.methods
            .joinWager()
            .accounts({
              admin: admin,
              wager: wagerKey,
              wagerVault: wagerVault,
              player: player,
              authority: publicKey!,
              // @ts-ignore
              token: tokenMint,
              // @ts-ignore
              authorityTokenAccount: authorityTokenAccount,
              // @ts-ignore
              wagerVaultTokenAccount: wagerVaultTokenAccount,
              // @ts-ignore
              tokenProgram: token.TOKEN_PROGRAM_ID,
              // @ts-ignore
              associatedTokenProgram: token.ASSOCIATED_TOKEN_PROGRAM_ID,
            })
            .instruction();
          joinWagerTx.add(joinWagerIx);
        }
        joinWagerTx.feePayer = publicKey!;

        const latestBlockHash =
          await program.provider.connection.getLatestBlockhash();
        joinWagerTx.recentBlockhash = latestBlockHash.blockhash;
        const sig = await wallet.sendTransaction(
          joinWagerTx,
          program.provider.connection
        );
        const tx = await program.provider.connection.confirmTransaction({
          blockhash: latestBlockHash.blockhash,
          lastValidBlockHeight: latestBlockHash.lastValidBlockHeight,
          signature: sig,
        });
        console.log("confirmed tx", tx);

        if (!tx.value.err) {
          let response = await ApiService.post("/challenge", {
            instanceId,
            wagered: true,
          });
          if (response && response.success) {
            let authToken = response.data.token;
            if (authToken) {
              StorageService.set("authToken", authToken);
              sendMessage("GameManager", "RegisterToken", authToken);
            } else {
              console.error("Invalid token");
            }
          } else {
            console.error(response.message);
          }
        } else {
          console.log("error", tx.value.err);
        }
      } catch (err) {
        console.error(err);
      }
    } else {
      let response = await ApiService.post("/challenge", { instanceId });
      if (response && response.success) {
        let authToken = response.data.token;
        if (authToken) {
          StorageService.set("authToken", authToken);
          sendMessage("GameManager", "RegisterToken", authToken);
        } else {
          console.error("Invalid token");
        }
      } else {
        console.error(response.message);
      }
    }
  }

  const GameStatusText = () => {
    if (gameData) {
      switch (gameData.status) {
        case GameStatus.Preparing:
        case GameStatus.Waiting:
          return (
            <Row className="justify-content-between">
              <Col>{gameData?.player1.ready ? "Ready" : "Not Ready"}</Col>
              <Col className="text-end">
                {gameData?.player2.ready ? "Ready" : "Not Ready"}
              </Col>
            </Row>
          );
        case GameStatus.Playing:
          return (
            <Row className="justify-content-between">
              <Col>Playing</Col>
              <Col className="text-center">{speed / 10} mph</Col>
              <Col className="text-end">Playing</Col>
            </Row>
          );
        case GameStatus.Intermission:
          return (
            <Row className="justify-content-between">
              <Col className="text-center">Intermission...</Col>
            </Row>
          );
        case GameStatus.GameOver:
        default:
          if (gameData?.winner && gameData?.winner?.result === GameResult.Forfeit) {
            return (
              <Row className="justify-content-center text-center">
                <Col>{gameData?.winner?.name} has won by Forfeit!</Col>
              </Row>
            );
          } else if (wagerInfo?.wagered) {
            return (
              <Row className="justify-content-center text-center">
                <Col>
                  {gameData?.winner?.name} has won {wagerInfo?.winnings} {wagerInfo?.wagerToken.symbol}!
                </Col>
              </Row>
            );
          } else {
            return (
              <Row className="justify-content-center text-center">
                <Col>
                  {gameData?.winner?.name} has won!
                </Col>
              </Row>
            );
            /*let player1Rematch = gameData?.player1.ready
              ? "Awaiting rematch"
              : "Waiting...";
            let player2Rematch = gameData?.player2.ready
              ? "Awaiting rematch"
              : "Waiting...";
            let player2Status =
              gameData?.player2.address.length > 0
                ? player2Rematch
                : "Player left";

            return (
              <Row className="justify-content-between">
                <Col>{player1Rematch}</Col>
                <Col className="text-center">Checking for rematch...</Col>
                <Col className="text-end">{player2Status}</Col>
              </Row>
            );*/
          }
      }
    } else {
      return (
        <Row className="justify-content-center">
          <Col className="text-center">Loading game data...</Col>
        </Row>
      );
    }
  };

  return (
    <>
      <Row className="justify-content-between mb-3">
        <Col>
          <button
            className="dg-btn dg-btn-mid-dark dg-btn-block"
            onClick={async () => {
              await handleLeaveGame();
            }}
          >
            Leave Game
          </button>
        </Col>
        <Col className="text-end d-none d-md-block">
          <button
            className="dg-btn dg-btn-mid-dark dg-btn-block"
            onClick={handleChallengeGame}
            disabled={!challengeable}
          >
            Challenge Game
          </button>
        </Col>
      </Row>
      <Card
        className="card-widget justify-content-center mb-3"
        id="scoreboard-widget"
      >
        <Card.Body>
          <Row className="g-0 d-none d-md-flex">
            <Col xs={2} className="text-center p-2">
              <img
                src={getProfilePicture(player1?.address)}
                alt="pfp"
                height="72"
              />
            </Col>
            <Col xs={3}>
              <span className="fs-5">
                {player1 ? (
                  <Link to={`/profile/${player1.address}`} target="_blank">
                    {player1.name}
                  </Link>
                ) : (
                  "Player 1"
                )}
              </span>
              <br />
              <span
                className="score text-center d-block fs-4 mt-2 p-1"
                id="player1"
              >
                {player1 ? gameData?.player1.score : 0} pts
              </span>
            </Col>
            <Col xs={2} className="text-center fs-3">
              VS
              <br />
              {wagerInfo?.wagered ? (
                <span className="fs-5">
                  {wagerInfo?.winnings} {wagerInfo?.wagerToken.symbol}
                  <br />
                  <span className="fs-6">Prize</span>
                </span>
              ) : (
                ""
              )}
            </Col>
            <Col xs={3} className="text-end">
              <span className="fs-5">
                {player2 && player2.address.length > 0 ? (
                  <Link to={`/profile/${player2.address}`} target="_blank">
                    {player2.name}
                  </Link>
                ) : (
                  "Player 2"
                )}
              </span>
              <br />
              <div className="score text-center fs-4 mt-2 p-1" id="player2">
                {player2 ? gameData?.player2.score : 0} pts
              </div>
            </Col>
            <Col xs={2} className="text-center p-2">
              <img
                src={getProfilePicture(player2?.address)}
                alt="pfp"
                height="72"
              />
            </Col>
          </Row>
          <Row className="g-0 d-flex d-md-none">
            <Col xs={6} className="text-center p-2">
              <img
                src={getProfilePicture(player1?.address)}
                alt="pfp"
                height="72"
              />
              <br />
              <span className="fs-5">
                {player1 ? (
                  <Link to={`/profile/${player1.address}`} target="_blank">
                    {player1.name}
                  </Link>
                ) : (
                  "Player 1"
                )}
              </span>
              <br />
              <div className="score text-center fs-4 mt-2 p-1" id="player1">
                {player1 ? gameData?.player1.score : 0} pts
              </div>
            </Col>
            <Col xs={6} className="text-center p-2">
              <img
                src={getProfilePicture(player2?.address)}
                alt="pfp"
                height="72"
              />
              <br />
              <span className="fs-5">
                {player2 && player2.address.length > 0 ? (
                  <Link to={`/profile/${player2.address}`} target="_blank">
                    {player2.name}
                  </Link>
                ) : (
                  "Player 2"
                )}
              </span>
              <br />
              <div className="score text-center fs-4 mt-2 p-1" id="player2">
                {player2 ? gameData?.player2.score : 0} pts
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card className="card-widget" id="game-widget">
        <Card.Body className="m-0 p-0">
          <div className="game-table mx-auto" id="unity">
            <Fragment>
              {!isLoaded && (
                <p className="full-center">
                  Loading Application... {Math.round(loadingProgression * 100)}%
                </p>
              )}
              <Unity
                unityProvider={unityProvider}
                style={{
                  visibility: isLoaded ? "visible" : "hidden",
                  cursor: "crosshair",
                }}
              />
            </Fragment>
          </div>
        </Card.Body>
        <Card.Footer>
          <GameStatusText />
        </Card.Footer>
      </Card>
    </>
  );
};

export default PongGameComponent;
